import { useMemo } from 'react';

/**
 * Hook to convert text from ISO-8859-1 (Latin1) to UTF-8
 * Used primarily to fix encoding issues from database data
 */
export function useEncodingConverter(text: string | null | undefined): string {
  return useMemo(() => {
    return convertISO88591ToUTF8(text);
  }, [text]);
}

/**
 * Direct conversion utility (non-React)
 * Can be used in contexts that are not React components
 */
export function convertISO88591ToUTF8(text: string | null | undefined): string {
  if (!text) return '';
  
  try {
    if (!/[^\x00-\x7F]/.test(text) || isValidUTF8(text)) {
      return text;
    }
    
    return decodeURIComponent(escape(text));
  } catch (error) {
    try {
      const bytes = Array.from(text, char => char.charCodeAt(0) & 0xFF);
      return new TextDecoder('utf-8').decode(new Uint8Array(bytes));
    } catch (fallbackError) {
      console.warn('Erro na conversão de encoding para:', text, error);
      return text;
    }
  }
}

/**
 * Check if a string is valid UTF-8
 */
function isValidUTF8(str: string): boolean {
  try {
    return str === decodeURIComponent(encodeURIComponent(str));
  } catch {
    return false;
  }
}

/**
 * Alternative approach using TextDecoder (modern browsers)
 * More robust but may not work in very old browsers
 */
export function useEncodingConverterModern(text: string | null | undefined): string {
  return useMemo(() => {
    if (!text) return '';
    
    try {
      const bytes = new Uint8Array([...text].map(char => char.charCodeAt(0) & 0xFF));
      return new TextDecoder('utf-8').decode(bytes);
    } catch (error) {
      console.warn('Erro na conversão moderna de encoding para:', text, error);
      try {
        return decodeURIComponent(escape(text));
      } catch (fallbackError) {
        console.warn('Erro no fallback de encoding para:', text, fallbackError);
        return text;
      }
    }
  }, [text]);
}
